import { useI18n } from '../../i18n';
// ============================================
// CategoriesPage
// ============================================

import { useState, useMemo, useCallback } from 'react';
import { PageHeader, ActionButton, PageContainer, ExportButton } from '../../components/shared';
import { PlusIcon } from '../../components/icons';
import { useExport } from '../../hooks';
import {
  useCategories,
  useCategoryStats,
  useCategory,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  useBulkUpdateCategories,
} from '../../hooks';
import { isApiError } from '@conai/api-client';
import type { Category, CreateCategoryRequest, UpdateCategoryRequest } from '@conai/shared-types';

import { CategoryStatsRow } from './components/CategoryStatsRow';
import { CategoryFilterBar } from './components/CategoryFilterBar';
import { CategoryTable, type CategoryTableItem } from './components/CategoryTable';
import { CategoryFormModal, type CategoryFormData, emptyForm } from './components/CategoryFormModal';
import { CategoryDeleteModal } from './components/CategoryDeleteModal';

// ---- types ----
type ListEnvelope<T> = {
  results?: T[]; items?: T[];
  data?: T[] | { results?: T[]; items?: T[]; data?: T[] };
  count?: number; total?: number; meta?: { count?: number };
};

type CategoryListItem = Category & {
  parent?: Category['parent'] | string | null;
  children_count?: number;
};

type ModalMode = 'create' | 'edit' | 'delete' | null;

// ---- helpers ----
function extractList<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[];
  const d = payload as ListEnvelope<T> | undefined;
  if (!d) return [];
  // PaginatedResponse uses 'results' — check it first
  if (Array.isArray(d.results)) return d.results;
  if (Array.isArray(d.items)) return d.items;
  if (Array.isArray(d.data)) return d.data;
  if (d.data && typeof d.data === 'object') {
    const nested = d.data as ListEnvelope<T>;
    return nested.results ?? nested.items ?? nested.data as T[] ?? [];
  }
  return [];
}

function extractCount<T>(payload: unknown, fallback: number): number {
  if (Array.isArray(payload)) return payload.length;
  const d = payload as ListEnvelope<T> | undefined;
  return d?.count ?? d?.total ?? d?.meta?.count ?? fallback;
}

function resolveParentId(cat: Partial<CategoryListItem>): string | null {
  if (cat.parent_id) return cat.parent_id;
  if (typeof cat.parent === 'string') return cat.parent;
  const r = cat.parent as Record<string, unknown> | null | undefined;
  return typeof r?.id === 'string' ? r.id : null;
}

/**
 * Số danh mục con — trường DERIVED, read-only (BE trả `children_count` ở
 * `GET /categories/`, xem docs/API_REFERENCE.md § List Categories). Không có
 * field tương ứng trong form Add/Edit là đúng thiết kế.
 *
 * Bug #66 (vòng 2): BỎ HẲN fallback "tự đếm danh mục con trên danh sách đang
 * hiển thị". Fallback đó chỉ thấy được TRANG HIỆN TẠI, nên danh mục có con nằm
 * ở trang khác bị báo thiếu — và sai kiểu đó không ai phát hiện vì nó vẫn ra một
 * con số trông hợp lý. BE giờ annotate ở tầng DB: số con THẬT trên toàn bộ danh
 * mục, không phụ thuộc phân trang, đã loại danh mục con xoá mềm.
 *
 * Trả `null` khi payload KHÔNG mang field (BE cũ) để cột hiện "—". Cố ý không
 * quy về 0: "chưa có dữ liệu" khác hẳn "thật sự không có con nào", và số 0 do BE
 * trả về phải hiện đúng là 0.
 *
 * `?tree=true` không có field này (nó lồng sẵn mảng `children`) — trang này chỉ
 * gọi list phẳng, không dùng chế độ tree, nên không cần nhánh xử lý riêng.
 */
function resolveChildrenCount(raw: Record<string, unknown>): number | null {
  const apiCount = raw.children_count;
  if (apiCount === undefined || apiCount === null) return null;
  const parsed = Number(apiCount);
  return Number.isFinite(parsed) ? parsed : null;
}

function toFormData(cat?: Partial<CategoryListItem>): CategoryFormData {
  if (!cat) return emptyForm;
  const raw = cat as unknown as Record<string, unknown>;
  const isActive = raw.is_active === true || raw.is_active === 'true' || raw.is_active === 1
    || raw.status === 'ACTIVE' || raw.status === 'active';
  return {
    name: cat.name ?? '',
    parentId: resolveParentId(cat) ?? '',
    sortOrder: String(cat.sort_order ?? 0),
    isActive,
    imageUrls: Array.isArray(cat.image_urls) ? cat.image_urls : [],
  };
}

function toCreatePayload(form: CategoryFormData): CreateCategoryRequest {
  return {
    name: form.name.trim(),
    parent_id: form.parentId || undefined,
    sort_order: Number(form.sortOrder),
    is_active: form.isActive ?? undefined,
    // Chưa chọn ảnh thì KHÔNG gửi field — để BE dùng mặc định của nó, thay vì
    // ép mảng rỗng vào bản ghi mới.
    image_urls: form.imageUrls.length > 0 ? form.imageUrls : undefined,
  };
}

function toUpdatePayload(form: CategoryFormData): UpdateCategoryRequest {
  return {
    name: form.name.trim(),
    parent_id: form.parentId || null,
    sort_order: Number(form.sortOrder),
    is_active: form.isActive ?? undefined,
    // Ở update thì mảng rỗng LÀ ý định "xoá hết ảnh" — gửi nguyên, BE hiểu
    // (mảng rỗng và null đều hợp lệ).
    image_urls: form.imageUrls,
  };
}

export function CategoriesPage() {
  const { t } = useI18n();
  const [currentPage, setCurrentPage] = useState(1);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [editInitialIsActive, setEditInitialIsActive] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const pageSize = 20;

  const { triggerExport, isExporting } = useExport({ endpoint: '/categories/', filePrefix: 'categories' });

  const { data: categoriesData, isLoading, error } = useCategories({ page: currentPage, limit: pageSize, search: searchQuery || undefined, isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined });
  const { data: categoryStats } = useCategoryStats();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const bulkUpdateCategories = useBulkUpdateCategories();

  const { data: categoryDetailData, isLoading: isLoadingDetail } = useCategory(activeCategoryId ?? '', {
    enabled: !!activeCategoryId && modalMode === 'edit',
  });

  const categoryList: CategoryListItem[] = useMemo(() => extractList<CategoryListItem>(categoriesData), [categoriesData]);
  const categoriesCount = useMemo(() => extractCount<CategoryListItem>(categoriesData, categoryList.length), [categoriesData, categoryList.length]);

  /**
   * Xếp bảng theo CÂY, không theo tầng.
   *
   * Bản cũ sắp `level → sort_order → name`, tức gom hết danh mục gốc lên trên
   * rồi mới tới toàn bộ danh mục cấp 2 — con nằm cách cha cả chục dòng, nhìn
   * thụt lề không đoán được nó là con của ai. Giờ duyệt sâu (DFS): mỗi danh mục
   * con đứng NGAY SAU cha nó, anh em cùng cha xếp theo `sort_order → name`.
   *
   * Hệ quả cố ý cho nút lên/xuống (#17): đổi chỗ hai danh mục cha thì cả cụm
   * con đi theo, vì vị trí của con suy ra từ vị trí của cha chứ không tự đứng
   * riêng. Con chỉ hoán đổi trong phạm vi anh em cùng cha — chặn ở `findSibling`
   * (bảng) và vòng dò hàng xóm trong `handleMove`, cả hai đều lọc theo
   * `parentId` nên bước qua một cụm con không bao giờ bắt nhầm.
   */
  const tableData: CategoryTableItem[] = useMemo(() => {
    const nameById = new Map<string, string>();
    const byId = new Map<string, CategoryListItem>();
    for (const cat of categoryList) {
      nameById.set(cat.id, cat.name);
      byId.set(cat.id, cat);
    }

    const childrenOf = new Map<string, CategoryListItem[]>();
    const roots: CategoryListItem[] = [];
    for (const cat of categoryList) {
      const pid = resolveParentId(cat);
      // Cha nằm ở TRANG KHÁC (list phân trang 20 dòng) -> xếp như dòng gốc.
      // Thà thụt lề hụt một bậc còn hơn giấu mất danh mục khỏi bảng.
      if (pid && byId.has(pid)) {
        const bucket = childrenOf.get(pid);
        if (bucket) bucket.push(cat);
        else childrenOf.set(pid, [cat]);
      } else {
        roots.push(cat);
      }
    }

    const bySortThenName = (a: CategoryListItem, b: CategoryListItem) =>
      (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.name.localeCompare(b.name);
    roots.sort(bySortThenName);
    for (const bucket of childrenOf.values()) bucket.sort(bySortThenName);

    const toItem = (cat: CategoryListItem, level: number): CategoryTableItem => {
      const pid = resolveParentId(cat);
      // Normalize is_active: API returns status: "ACTIVE"/"INACTIVE" string
      const raw = cat as unknown as Record<string, unknown>;
      const isActive = raw.is_active === true || raw.is_active === 'true' || raw.is_active === 1
        || raw.status === 'ACTIVE' || raw.status === 'active';
      const parentNameFromApi = typeof raw.parent_name === 'string' ? raw.parent_name : null;
      return {
        id: cat.id, name: cat.name, slug: cat.slug,
        parentId: pid, parentName: pid ? (parentNameFromApi ?? nameById.get(pid) ?? pid) : '-',
        childrenCount: resolveChildrenCount(raw),
        // Tên các con CÓ MẶT TRÊN TRANG NÀY, không phải toàn bộ con. Số thật
        // vẫn là `childrenCount` của BE; bảng lấy hiệu hai số để hiện "+n" —
        // đây đúng là cái bẫy phân trang mà bug #66 đã gỡ khỏi phần ĐẾM, nên
        // tuyệt đối không lấy `childrenNames.length` làm số con.
        childrenNames: (childrenOf.get(cat.id) ?? []).map((c) => c.name),
        isActive, sortOrder: cat.sort_order ?? 0,
        level,
      };
    };

    const out: CategoryTableItem[] = [];
    const visited = new Set<string>();
    const walk = (nodes: CategoryListItem[], level: number) => {
      for (const cat of nodes) {
        if (visited.has(cat.id)) continue;
        visited.add(cat.id);
        out.push(toItem(cat, level));
        walk(childrenOf.get(cat.id) ?? [], Math.min(6, level + 1));
      }
    };
    walk(roots, 0);

    // Vòng cha-con (BE chặn bằng `category_cycle`, nhưng dữ liệu cũ có thể lọt):
    // cụm đó không có gốc nên DFS không chạm tới. Đổ nốt ra cuối bảng thay vì
    // để dòng biến mất không một lời báo.
    for (const cat of categoryList) {
      if (visited.has(cat.id)) continue;
      visited.add(cat.id);
      out.push(toItem(cat, 0));
    }
    return out;
  }, [categoryList]);

  const filteredData = tableData;

  const categoryDetail = useMemo(() => {
    const raw = categoryDetailData as CategoryListItem | { data?: CategoryListItem } | undefined;
    if (!raw) return null;
    if ('data' in raw && raw.data) return raw.data;
    return raw as CategoryListItem;
  }, [categoryDetailData]);

  const activeCategoryFromList = useMemo(() => categoryList.find((c) => c.id === activeCategoryId) ?? null, [categoryList, activeCategoryId]);

  // Overview stats come from the server (GET /categories/stats/): aggregate
  // over the WHOLE taxonomy, independent of the search/status filters. They
  // refresh on create/update/delete via `categories.all` invalidation.
  const stats = useMemo(() => ({
    total: categoryStats?.total ?? 0,
    active: categoryStats?.active ?? 0,
    root: categoryStats?.root ?? 0,
    leaf: categoryStats?.leaf ?? 0,
  }), [categoryStats]);

  const formInitialData = useMemo(() => {
    if (modalMode === 'create') return emptyForm;
    const source = categoryDetail ?? activeCategoryFromList;
    if (source) return toFormData(source);
    // fallback: use editInitialIsActive so status is correct even before detail loads
    return { ...emptyForm, isActive: editInitialIsActive };
  }, [modalMode, categoryDetail, activeCategoryFromList, editInitialIsActive]);

  const closeModal = useCallback(() => {
    setModalMode(null);
    setActiveCategoryId(null);
  }, []);

  const handleAddCategory = useCallback(() => {
    setActiveCategoryId(null);
    setModalMode('create');
  }, []);

  const handleEdit = useCallback((item: CategoryTableItem) => {
    setActiveCategoryId(item.id);
    setModalMode('edit');
    // store isActive from list item to use as initial value before detail loads
    setEditInitialIsActive(item.isActive);
  }, []);

  const handleDelete = useCallback((item: CategoryTableItem) => {
    setActiveCategoryId(item.id);
    setModalMode('delete');
  }, []);

  /**
   * Đổi thứ tự hiển thị (sheet 2 mục #17) — POST /categories/bulk-update/.
   *
   * MỘT request nguyên tử cho cả hai dòng đổi chỗ, chạy trong transaction phía
   * BE. (Bản trước dùng 2 lần PATCH tuần tự vì tưởng BE không có endpoint bulk;
   * BE xác nhận 03/09 là có sẵn — đã bỏ cách cũ.)
   *
   * Chỉ gửi HAI dòng thay đổi chứ không gửi cả danh sách: gửi cả trang là ghi
   * đè `sort_order` của những dòng người khác vừa sửa.
   */
  const [isReordering, setIsReordering] = useState(false);
  const [reorderError, setReorderError] = useState<string | null>(null);

  const handleMove = useCallback(
    async (item: CategoryTableItem, direction: 'up' | 'down') => {
      if (isReordering) return;
      const index = tableData.findIndex((c) => c.id === item.id);
      if (index === -1) return;

      const step = direction === 'up' ? -1 : 1;
      let neighbourIndex = -1;
      for (let i = index + step; i >= 0 && i < tableData.length; i += step) {
        if (tableData[i].level === item.level && tableData[i].parentId === item.parentId) {
          neighbourIndex = i;
          break;
        }
      }
      if (neighbourIndex === -1) return;

      const neighbour = tableData[neighbourIndex];
      // Hai dòng cùng sort_order (dữ liệu cũ) thì hoán đổi số là không đổi gì —
      // đẩy lệch một bậc để thao tác có tác dụng thật.
      const nextForItem =
        item.sortOrder === neighbour.sortOrder
          ? neighbour.sortOrder + (direction === 'up' ? -1 : 1)
          : neighbour.sortOrder;

      setIsReordering(true);
      setReorderError(null);
      try {
        await bulkUpdateCategories.mutateAsync([
          { id: item.id, sort_order: nextForItem },
          ...(item.sortOrder === neighbour.sortOrder
            ? []
            : [{ id: neighbour.id, sort_order: item.sortOrder }]),
        ]);
      } catch (err) {
        // 4 mã lỗi BE chốt 03/09 — phân biệt để nói đúng việc cần làm, thay vì
        // một câu "lỗi" chung chung cho cả bốn nguyên nhân khác hẳn nhau.
        const reason = isApiError(err)
          ? ((err.data as { errors?: { reason?: string }; reason?: string } | undefined)?.errors
              ?.reason ??
            (err.data as { reason?: string } | undefined)?.reason ??
            '')
          : '';
        if (reason === 'categories_not_found' || reason === 'parent_categories_not_found') {
          setReorderError(t('categoryReorderStale'));
        } else if (reason === 'category_cycle') {
          setReorderError(t('categoryReorderCycle'));
        } else if (reason === 'duplicate_category_ids') {
          setReorderError(t('categoryReorderDuplicate'));
        } else {
          setReorderError(t('categoryReorderFailed'));
        }
      } finally {
        setIsReordering(false);
      }
    },
    [isReordering, tableData, bulkUpdateCategories, t]
  );

  const handleFormSubmit = useCallback(async (data: CategoryFormData) => {
    if (modalMode === 'create') {
      await createCategory.mutateAsync(toCreatePayload(data));
    } else if (modalMode === 'edit' && activeCategoryId) {
      await updateCategory.mutateAsync({ id: activeCategoryId, data: toUpdatePayload(data) });
    }
    closeModal();
  }, [modalMode, activeCategoryId, createCategory, updateCategory, closeModal]);

  const handleConfirmDelete = useCallback(async () => {
    if (!activeCategoryId) return;
    await deleteCategory.mutateAsync(activeCategoryId);
    closeModal();
  }, [activeCategoryId, deleteCategory, closeModal]);

  const activeCategoryName = categoryDetail?.name ?? activeCategoryFromList?.name ?? 'this category';

  return (
    <PageContainer>
      <PageHeader
        title={t('categories')}
        subtitle={t('manageCategoryStructureAndProductTa')}
        actions={
          <div className="flex items-center gap-2">
            <ExportButton onExport={triggerExport} isLoading={isExporting} />
            <ActionButton variant="primary" icon={<PlusIcon size={16} />} onClick={handleAddCategory}>
              {t('addCategory')}
            </ActionButton>
          </div>
        }
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadCategories')}</p>
          <p className="mt-1 text-red-600">{error instanceof Error ? error.message : 'An error occurred.'}</p>
        </div>
      )}

      <div className="mt-6">
        <CategoryStatsRow stats={stats} />
      </div>

      <div className="mt-6">
        <CategoryFilterBar
          searchQuery={searchQuery}
          statusFilter={statusFilter}
          onSearchChange={(v) => { setSearchQuery(v); setCurrentPage(1); }}
          onStatusFilterChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
        />
      </div>

      {/* Lỗi sắp xếp — nói rõ việc cần làm (reload / thao tác không hợp lệ),
          không gộp 4 nguyên nhân vào một câu "lỗi" */}
      {reorderError && (
        <div className="mt-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
          {reorderError}
        </div>
      )}

      <div className="mt-4">
        <CategoryTable
          data={filteredData}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onMove={handleMove}
          isReordering={isReordering}
          totalItems={categoriesCount}
          currentPage={currentPage}
          pageSize={pageSize}
          totalPages={Math.ceil(categoriesCount / pageSize)}
          onPageChange={setCurrentPage}
        />
      </div>

      {(modalMode === 'create' || modalMode === 'edit') && (
        <CategoryFormModal
          key={`${modalMode}-${activeCategoryId ?? 'new'}-${modalMode === 'edit' ? editInitialIsActive : 'create'}`}
          mode={modalMode}
          initialData={formInitialData}
          activeCategoryId={activeCategoryId}
          initialParentLabel={tableData.find((t) => t.id === activeCategoryId)?.parentName !== '-' ? tableData.find((t) => t.id === activeCategoryId)?.parentName : undefined}
          isLoading={modalMode === 'edit' && isLoadingDetail && !activeCategoryFromList}
          isSubmitting={createCategory.isPending || updateCategory.isPending}
          onSubmit={handleFormSubmit}
          onClose={closeModal}
        />
      )}

      {modalMode === 'delete' && (
        <CategoryDeleteModal
          categoryName={activeCategoryName}
          isDeleting={deleteCategory.isPending}
          onConfirm={handleConfirmDelete}
          onClose={closeModal}
        />
      )}
    </PageContainer>
  );
}
